/**
 * Automations fallback fixtures — Bill prototype 2026-05-30 (SUBA / CMDS /
 * SCHED / ALERTS arrays). Drives /subagents, /commands, /scheduled, /alerts
 * when real automation tables are empty or the caller is anonymous.
 */

import {
  DEMO_AGENTS,
  DEMO_ALERTS,
  DEMO_COMMANDS,
  DEMO_SCHEDULES,
} from '@/lib/fixtures/demo-brand'

export type AutomationChannel =
  | 'search'
  | 'social'
  | 'tv'
  | 'programmatic'
  | 'audio'
  | 'retail'
  | 'creative'
  | 'analyst'
  | 'workspace'
  | 'all'

export interface SubagentRow {
  key: string
  name: string
  channel: AutomationChannel
  role: string
  actsLabel: string
  lastRun: string
  enabled: boolean
}

export const FALLBACK_SUBAGENTS: SubagentRow[] = DEMO_AGENTS.map((agent) => ({
  key: agent.key,
  name: agent.name,
  channel: agent.channel,
  role: agent.role,
  actsLabel: agent.actsLabel,
  lastRun: agent.lastRun,
  enabled: agent.enabled,
}))

export interface CommandRow {
  key: string
  name: string
  description: string
  /** Stroke-icon name from @hugeicons-pro/core-stroke-rounded, resolved in the component. */
  iconKey: CommandIconKey
  lastRun: string
}

export type CommandIconKey =
  | 'sparkles' | 'shield' | 'balance' | 'pause' | 'filter' | 'users'
  | 'sliders' | 'route' | 'clock' | 'activity' | 'binoculars' | 'file'

const COMMAND_ICON_BY_ID: Record<string, CommandIconKey> = {
  'demo-cmd-rebalance': 'balance',
  'demo-cmd-pause-weak': 'pause',
  'demo-cmd-negative-keyword': 'filter',
  'demo-cmd-fraud-sweep': 'shield',
  'demo-cmd-refresh-creative': 'sparkles',
  'demo-cmd-weekly-digest': 'file',
}

function commandIconKey(id: string): CommandIconKey {
  return COMMAND_ICON_BY_ID[id] ?? 'sparkles'
}

function commandKey(id: string): string {
  return id.replace(/^demo-cmd-/, '')
}

export const FALLBACK_COMMANDS: CommandRow[] = DEMO_COMMANDS.map((command) => ({
  key: commandKey(command.id),
  name: command.name,
  description: command.description,
  iconKey: commandIconKey(command.id),
  lastRun: command.runsLast7d > 0 ? `${command.runsLast7d} runs this week` : 'Never',
}))

export interface ScheduleRow {
  key: string
  name: string
  iconKey: CommandIconKey
  channel: AutomationChannel
  cadence: string
  nextRun: string
  enabled: boolean
}

function formatScheduleTime(value: string): string {
  if (!value) return 'Triggered'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

function scheduleChannel(commandName: string): AutomationChannel {
  const command = DEMO_COMMANDS.find((item) => item.name === commandName)
  if (!command || command.channels.length !== 1) return 'all'
  return command.channels[0]
}

function scheduleIconKey(commandName: string): CommandIconKey {
  const command = DEMO_COMMANDS.find((item) => item.name === commandName)
  return command ? commandIconKey(command.id) : 'clock'
}

export const FALLBACK_SCHEDULES: ScheduleRow[] = DEMO_SCHEDULES.map((schedule) => ({
  key: schedule.id,
  name: schedule.commandName,
  iconKey: scheduleIconKey(schedule.commandName),
  channel: scheduleChannel(schedule.commandName),
  cadence: schedule.cadence,
  nextRun: formatScheduleTime(schedule.nextRunIso),
  enabled: schedule.status !== 'failing',
}))

export type AlertSeverity = 'crit' | 'warn' | 'done' | 'info'
export type AlertIconKey =
  | 'filter' | 'trending-up' | 'trending-down' | 'key' | 'users' | 'gauge'
  | 'activity' | 'plug-off' | 'shield' | 'wallet' | 'file'

export interface AlertRow {
  id: string
  kind?: string | null
  eventType?: string | null
  channel: AutomationChannel
  severity: AlertSeverity
  iconKey: AlertIconKey
  title: string
  detail: string
  time: string
  createdAt?: string
  action: string  // empty = no action button
  primaryActionLabel?: string | null
  primaryActionHref?: string | null
  secondaryActionLabel?: string | null
  secondaryActionHref?: string | null
  unread: boolean
}

export const FALLBACK_ALERTS: AlertRow[] = DEMO_ALERTS.map((alert) => ({
  id: alert.id,
  channel: alert.channel,
  severity: alert.severity,
  iconKey: alert.iconKey as AlertIconKey,
  title: alert.title,
  detail: alert.detail,
  time: alert.time,
  action: alert.action,
  unread: alert.unread,
}))

export interface AutomationChannelMeta {
  label: string
  hex: string
  /** 135deg two-stop gradient for agent icon tiles. */
  gradient: string
  /** Tint for status pills (e.g. chchip). */
  tint: string
  ink: string
}

export const AUTOMATION_CHANNEL_META: Record<AutomationChannel, AutomationChannelMeta> = {
  search:       { label: 'Search',       hex: '#A8780C', gradient: 'linear-gradient(135deg, #E0A82E, #FFD95C)', tint: '#FFE8A3', ink: '#7A5C00' },
  social:       { label: 'Social',       hex: '#2D6CDF', gradient: 'linear-gradient(135deg, #2D6CDF, #7FB0FF)', tint: '#A6E1FF', ink: '#0A3A5C' },
  tv:           { label: 'TV / CTV',     hex: '#7A5BD6', gradient: 'linear-gradient(135deg, #7A5BD6, #B9A6FF)', tint: '#D4C7FF', ink: '#2D1B5E' },
  programmatic: { label: 'Programmatic', hex: '#0A8A6E', gradient: 'linear-gradient(135deg, #0A8A6E, #2BC39A)', tint: '#C7F5DD', ink: '#0A3D29' },
  audio:        { label: 'Audio',        hex: '#E0762D', gradient: 'linear-gradient(135deg, #E0762D, #FFB36B)', tint: '#FFE0B5', ink: '#5C3300' },
  retail:       { label: 'Retail',       hex: '#C2185B', gradient: 'linear-gradient(135deg, #FF8FB0, #FFC2D4)', tint: '#FFD1DC', ink: '#7A1F40' },
  creative:     { label: 'Creative',     hex: '#C2185B', gradient: 'linear-gradient(135deg, #C2185B, #FF8FB0)', tint: '#FFD1DC', ink: '#7A1F40' },
  analyst:      { label: 'Analyst',      hex: '#1A1A1A', gradient: 'linear-gradient(135deg, #1A1A1A, #3A3A36)', tint: '#F1EFE8', ink: '#1A1A1A' },
  workspace:    { label: 'Workspace',    hex: '#006241', gradient: 'linear-gradient(135deg, #006241, #2BC39A)', tint: '#E6F2EC', ink: '#0A6E54' },
  all:          { label: 'All channels', hex: '#1A1A1A', gradient: 'linear-gradient(135deg, #1A1A1A, #3A3A36)', tint: '#F1EFE8', ink: '#1A1A1A' },
}
